import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PublicMarketingShell from "../components/PublicMarketingShell";
import {
  publicCardHoverSx,
  publicCardSx,
  publicSubtitleSx,
  publicTitleSx,
  PM_COLORS,
} from "../constants/publicMarketingTheme";
import { resolveUploadUrl } from "../utils/resolveUploadUrl";

function formatPostDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get("/api/public/blog-posts");
        if (!cancelled) setPosts(data.posts || []);
      } catch (e) {
        if (!cancelled) {
          setError(
            e.response?.data?.error ||
              e.message ||
              "Could not load blog posts.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PublicMarketingShell>
      <Container maxWidth="lg">
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              ...publicTitleSx,
              letterSpacing: -0.5,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Blog
          </Typography>
          <Typography sx={{ ...publicSubtitleSx, mt: 1.5, maxWidth: 720 }}>
            News, product updates, and insights from the JD Integrations team.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : error ? (
          <Typography color="error.light">{error}</Typography>
        ) : posts.length === 0 ? (
          <Box
            sx={{
              ...publicCardSx,
              p: 4,
              textAlign: "center",
            }}
          >
            <Typography sx={publicSubtitleSx}>
              No posts yet. Check back soon.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {posts.map((post) => {
              const coverSrc = post.cover_image_url
                ? resolveUploadUrl(post.cover_image_url)
                : "";
              const dateLabel = formatPostDate(
                post.published_at || post.created_at,
              );

              return (
                <Grid key={post.id} size={{ xs: 12, md: 6 }}>
                  <Card
                    elevation={0}
                    sx={{
                      ...publicCardSx,
                      ...publicCardHoverSx,
                      height: "100%",
                      overflow: "hidden",
                      p: 0,
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                      }}
                    >
                      {coverSrc ? (
                        <CardMedia
                          component="img"
                          image={coverSrc}
                          alt=""
                          sx={{ aspectRatio: "16 / 9", objectFit: "cover" }}
                        />
                      ) : null}
                      <CardContent
                        sx={{
                          flex: 1,
                          px: { xs: 2.5, sm: 3 },
                          py: { xs: 2.5, sm: 3 },
                        }}
                      >
                        {dateLabel ? (
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 1.5,
                              fontSize: "0.875rem",
                              color: PM_COLORS.textMuted,
                            }}
                          >
                            {dateLabel}
                            <Box
                              component="span"
                              sx={{ mx: 0.75, opacity: 0.7 }}
                            >
                              ·
                            </Box>
                            JD Integrations
                          </Typography>
                        ) : null}
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{
                            fontWeight: 700,
                            color: PM_COLORS.textPrimary,
                            lineHeight: 1.35,
                            mb: 1.25,
                          }}
                        >
                          {post.title}
                        </Typography>
                        {post.excerpt ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: publicSubtitleSx.color,
                              lineHeight: 1.65,
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {post.excerpt}
                          </Typography>
                        ) : null}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </PublicMarketingShell>
  );
}
